import React from "react";
import {
  Box,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useTranslation } from "react-i18next";
import CommonModal from "../common/CommonModal";
import { HomeworkResponseDto, downloadHomeworkAttachment } from "../../hooks/useHomework";

const formatFileSize = (bytes: number): string => {
  if (!bytes) return "0 KB";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

interface HomeworkViewModalProps {
  open: boolean;
  onClose: () => void;
  homework?: HomeworkResponseDto;
  loading?: boolean;
}

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {value ?? "-"}
    </Typography>
  </Grid>
);

const HomeworkViewModal: React.FC<HomeworkViewModalProps> = ({ open, onClose, homework, loading }) => {
  const { t } = useTranslation();

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={t("homework_details") || "Homework Details"}
      maxWidth="sm"
      fullWidth
      hideActions
    >
      {loading || !homework ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Grid container spacing={2}>
            <DetailRow label={t("session") || "Session"} value={homework.sessionName} />
            <DetailRow label={t("class") || "Class"} value={homework.className} />
            <DetailRow label={t("subject") || "Subject"} value={homework.subjectName} />
            <DetailRow label={t("date_of_assignment") || "Date of Assignment"} value={homework.assignmentDate} />
            <DetailRow label={t("due_date") || "Due Date"} value={homework.dueDate} />
            <DetailRow label={t("assigned_by") || "Assigned By"} value={homework.assignedByName} />
            <DetailRow
              label={t("assign_to") || "Assign To"}
              value={
                homework.assignType === "ALL"
                  ? t("all_students") || "All Students"
                  : `${t("select_students") || "Selected Students"} (${homework.studentIds?.length ?? 0})`
              }
            />
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="caption" color="text.secondary">
            {t("title") || "Title"}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
            {homework.title}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {t("description_instructions") || "Description / Instructions"}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
            {homework.description}
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("attachments") || "Attachments"}
            {homework.attachments?.length ? (
              <Chip size="small" label={homework.attachments.length} sx={{ ml: 1 }} />
            ) : null}
          </Typography>
          {homework.attachments && homework.attachments.length > 0 ? (
            <List dense sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
              {homework.attachments.map((att) => (
                <ListItem
                  key={att.id}
                  secondaryAction={
                    <IconButton
                      size="small"
                      onClick={() => downloadHomeworkAttachment(att.fileName, att.originalFileName)}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <InsertDriveFileIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={att.originalFileName} secondary={formatFileSize(att.fileSize)} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("no_attachments") || "No attachments"}
            </Typography>
          )}
        </Box>
      )}
    </CommonModal>
  );
};

export default HomeworkViewModal;
